var AbstractVideoPlayer         = require("app/MediaPlayer/Tech/Abstract/AbstractVideoPlayer"),
    Loader                      = require("app/MediaPlayer/Tech/RadiantMediaPlayer/Loader"),
    circularSafeJSONStringify   = require("app/utils/circularSafeJSONStringify"),
    config                      = require("app/config"),
    defaultVideoSetup           = require("app/ui/radiant/defaultVideoSetup"),
    frameThrottle               = require("enyo-perf/utils").frameThrottle,
    get                         = require("object-path").get,
    getAdSetup                  = require("app/ui/radiant/getAdSetup"),
    getCastingSetup             = require("app/ui/radiant/getCastingSetup"),
    getContentMetadataSetup     = require("app/ui/radiant/getContentMetadataSetup"),
    getGoogleAnalyticsSetup     = require("app/ui/radiant/getGoogleAnalyticsSetup"),
    getOffsetStartPositionSetup = require("app/ui/radiant/getOffsetStartPositionSetup"),
    getI18nSetup                = require("app/ui/radiant/getI18nSetup"),
    getLogoSetup                = require("app/ui/radiant/getLogoSetup"),
    getSrcSetup                 = require("app/ui/radiant/getSrcSetup"),
    kind                        = require("enyo/kind"),
    logger                      = require("enyo/logger"),
    themeVars                   = require("app/theme/variables.js"),
    utils                       = require("enyo/utils");

require("./Player.less");

module.exports = kind(/** @lends  app.MediaPlayer.RadiantMediaPlayer.VideoPlayer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.RadiantMediaPlayer.VideoPlayer",

    /**
     * @private
     */
    kind: AbstractVideoPlayer,

    /**
     * @private
     */
    classes: "app-media-player-radiant-media-player-video-player",

    // Private State
    // --------------------------------

    /**
     * @type {boolean}
     * @private
     */
    isLoadingRadiantMP: false,

    /**
     * @type {boolean}
     * @private
     */
    isRadiantMPLoaded: false,

    /**
     * @private
     */
    changeMediaSourceOnInitialize: false,

    /**
     * @type {?String}
     * @private
     */
    autoplayMode: null,

    /**
     * @private
     */
    startCursor: 0,

    /**
     * @private
     */
    playOnSrcChanged: false,

    /**
     * @private
     */
    shouldShowControls: true,

    /**
     * The ID of the video that's currently playing.
     *
     * @type {string|null}
     * @private
     */
    mediaId: null,

    /**
     * @private
     */
    isSrcChanging: false,

    /**
     * @private
     */
    duration: -1,

    /**
     * @private
     */
    cursor: -1,

    /**
     * The Ad Type of the session that's currently playing.
     *
     * @type {string|null}
     * @private
     */
    adType: null,

    /**
     * The url that configures the ad Tag.
     *
     * @type {string|null}
     * @private
     */
    adTagUrl: null,

    /**
     * The pixel associated with the current ad profile.
     *
     * @type {string|null}
     * @private
     */
    smartclipPixel: null,

    // Public State
    // --------------------------------

    /**
     * This player can apply safe area styling to it's UI.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    supportsSafeArea: true,

    /**
     * @type {boolean}
     * @readonly
     * @public
     */
    isReusable: true,

    /**
     * @type  {boolean}
     * @private
     */
    hasPlayedAds: false,

    /**
     * @type  {number}
     * @readonly
     * @public
     */
    hasInitialized: -1,

    /**
     * @type  {number}
     * @readonly
     * @public
     */
    isReady: -1,

    /**
     * This value indicates whether video playback has reached the startCursor.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    hasStarted: false,

    /**
     * The state of this page.
     *
     * @type  {?Object}
     * @public
     */
    state: null,

    /**
     * @type {boolean}
     * @public
     */
    autoplay: true,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlaying: false,

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlayingAds: false,

    /**
     * @readonly
     * @public
     */
    playerState: "idle",

    // Implementation
    // --------------------------------

    /**
     * @type null|HTMLElement
     * @private
     */
    playerNode: null,

    /**
     * @type null|RadiantMP
     * @private
     */
    radiantMediaPlayer: null,

    /**
     * @private
     */
    components: [{
        name:    "player",
        classes: "app-media-player-radiant-media-player-video-player__player"
    }],

    /**
     * @private
     */
    observers: {
        update: ["state"]
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.onAdBlock                  = this.bindSafely(this.onAdBlock);
            this.onAdComplete               = this.bindSafely(this.onAdComplete);
            this.onAdContentResumeRequested = this.bindSafely(this.onAdContentResumeRequested);
            this.onAdPaused                 = this.bindSafely(this.onAdPaused);
            this.onAdPlaying                = this.bindSafely(this.onAdPlaying);
            this.onAutoplayModeDetected     = this.bindSafely(this.onAutoplayModeDetected);
            this.onBuffer                   = this.bindSafely(this.onBuffer);
            this.onDestroyCompleted         = this.bindSafely(this.onDestroyCompleted);
            this.onDurationChange           = this.bindSafely(this.onDurationChange);
            this.onEnded                    = this.bindSafely(this.onEnded);
            this.onError                    = this.bindSafely(this.onError);
            this.onLoadedMetadata           = this.bindSafely(this.onLoadedMetadata);
            this.onPause                    = this.bindSafely(this.onPause);
            this.onPlay                     = this.bindSafely(this.onPlay);
            this.onReady                    = this.bindSafely(this.onReady);
            this.onSeek                     = this.bindSafely(this.onSeek);
            this.onSrcChanged               = this.bindSafely(this.onSrcChanged);
            this.onSrcChanging              = this.bindSafely(this.onSrcChanging);
            this.onStopCompleted            = this.bindSafely(this.onStopCompleted);
            this.onTimeUpdate               = this.bindSafely(this.onTimeUpdate);
            this.update                     = frameThrottle(this.bindSafely(this.update));
            sup.apply(this, arguments);
            Loader.load();
        };
    }),

    /**
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            var node = this.hasNode(),
                playerNode;

            if (node) {
                this.playerNode      = playerNode = document.createElement("div");
                playerNode.className = "app-media-player-radiant-media-player-video-player__player";
                playerNode.id        = "rmp-" + utils.uid();
                node.appendChild(playerNode);
            }

            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var player = this.getRadiantMPInstance(),
                bounds;

            sup.apply(this, arguments);

            if (player) {
                bounds = this.getBounds();
                player.setPlayerSize(bounds.width, bounds.height);
                this.alignLogo();
            }
        };
    }),

    /**
     * @private
     */
    // setSafeArea: function() {},

    /**
     * @private
     */
    alignLogo: function() {
        var player          = this.getRadiantMPInstance(),
            playerNode      = this.playerNode,
            logoNode,
            $mediaElement,
            playerWidth,
            playerHeight,
            playerAspectRatio,
            videoWidth,
            videoHeight,
            videoAspectRatio,
            logo            = getLogoSetup() || {},
            logoAspectRatio = logo.logoAspectRatio,
            logoMinWidth    = logo.logoMinWidth,
            logoMaxWidth    = logo.logoMaxWidth,
            logoMinHeight   = logo.logoMinHeight,
            logoMaxHeight   = logo.logoMaxHeight,
            logoWidth,
            logoHeight,
            logoOffset,
            logoLeft,
            logoTop;

        // Check for logo node
        logoNode = playerNode ? playerNode.querySelector(".rmp-logo") : null;

        // No thing to do
        if (!logoNode || !player || !logo.logo) {
            return;
        }

        // Player dimensions
        playerWidth       = player.getPlayerWidth();
        playerHeight      = player.getPlayerHeight();
        playerAspectRatio = playerWidth / playerHeight;

        // Media native dimensions
        $mediaElement    = player.getHtmlMediaElement();
        videoWidth       = $mediaElement ? $mediaElement.videoWidth || playerWidth : playerWidth;
        videoHeight      = $mediaElement ? $mediaElement.videoHeight || playerHeight : playerHeight;
        videoAspectRatio = videoWidth / videoHeight;

        // Scale video dimensions to fit within the player as needed
        if (playerAspectRatio >= videoAspectRatio) {
            videoHeight = playerHeight;
            videoWidth  = videoHeight * videoAspectRatio;
        }
        else {
            videoWidth  = playerWidth;
            videoHeight = videoWidth / videoAspectRatio;
        }

        // Logo size
        if (logoAspectRatio >= 1) {
            logoWidth  = Math.min(
                Math.max(videoWidth * 0.033, logoMinWidth),
                logoMaxWidth
            );
            logoHeight = logoWidth / logoAspectRatio;
        }
        else {
            logoHeight = Math.min(
                Math.max(videoHeight * 0.033, logoMinHeight),
                logoMaxHeight
            );
            logoWidth  = logoHeight * logoAspectRatio;
        }

        // Logo offset
        logoOffset = Math.min(
            ((videoWidth + videoHeight) / 2) * 0.033,
            (logoMaxWidth + logoMaxHeight) / 2
        );
        logoLeft   = logoOffset * 2 + (playerWidth - videoWidth) / 2;
        logoTop    = logoOffset + (playerHeight - videoHeight) / 2;

        // Position and scale logo
        logoNode.style.width   = logoWidth + "px";
        logoNode.style.height  = logoHeight + "px";
        logoNode.style.left    = "calc(" + logoLeft + "px + " + themeVars.safeAreaInsetLeft + ")";
        logoNode.style.top     = "calc(" + logoTop + "px + " + themeVars.safeAreaInsetTop + ")";
        logoNode.style.display = "block";
    },

    /**
     * @private
     */
    update: function() {
        var state   = this.state || {},
            mediaId = get(state, "media.id", null);

        // No mediaId, nothing to play yet.
        if (!mediaId) {
            this.clearPlayer();
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        // Load the RadiantMP Player, initialize a new player if we don't
        // already have one or simply switch to the new media source if we
        // already have a fully initialized player.
        this.set("isLoadingRadiantMP", true);
        Loader.load()
            .then(this.bindSafely(function() {
                this.set("isLoadingRadiantMP", false);
                this.set("isRadiantMPLoaded", true);

                switch (this.hasInitialized) {
                    case -1:
                        this.initializePlayer();
                        break;
                    case 0:
                        this.set("changeMediaSourceOnInitialize", true);
                        break;
                    case 1:
                        this.changeMediaSource();
                        break;
                    default:
                        break;
                }
            }))
            .catch(this.bindSafely(function(error) {
                this.set("isLoadingRadiantMP", false);
                this.resetPlayerState();
                this.set("isReusable", false);

                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.controller.triggerHandler("onMediaPlayerError", {
                    type:    "player_setup_error",
                    message: error.message,
                    details: {
                        loaderError: error
                    }
                }, this);
            }));
    },

    /**
     * @param  {boolean}  wasLoading  The previous state.
     * @private
     */
    isLoadingRadiantMPChanged: function(wasLoading) {
        var isLoading = this.isLoadingRadiantMP;

        if (!wasLoading && isLoading) {
            this.controller.triggerHandler("onRequestShowLoadingGraphic");
        }
        else if (wasLoading && !isLoading) {
            this.controller.triggerHandler("onRequestHideLoadingGraphic");
        }
    },

    /**
     * Stops playback regardless of current ready state.
     *
     * @private
     */
    clearPlayer: function() {
        this.stop();
    },

    /**
     * Creates a new YouTube Player instance and makes it play the requested
     * video.
     *
     * @private
     */
    initializePlayer: function() {
        var $analyticsService = this.app.$.analyticsService,
            $i18nService      = this.app.$.i18nService,
            $privacyService   = this.app.$.privacyService,
            state             = this.state || {},
            media             = get(state, "media", {}),
            asset             = get(state, "asset", {}),
            session           = get(state, "watchingSession", {}),
            params            = get(state, "params", {}),
            setup,
            bounds            = this.getBounds(),
            player;

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);
        this.set("hasInitialized", 0);

        // Build player setup
        setup = Object.assign(
            {},
            defaultVideoSetup,
            {
                autoplay:                        false,
                requestAutoplayCapabilitiesData: true,
                quickForward:                    window.innerWidth >= 479 ? 10 : false,
                quickRewind:                     window.innerWidth >= 479 ? 10 : false,
                viewableAutoplay:                false,
                viewablePlayPause:               false,
                width:                           bounds.width,
                height:                          bounds.height,
                pathToRmpFiles:                  config.radiantMediaPlayer.pathToRmpFiles,
                customIconsLoc:                  "/img/RadiantMP/icons.json"
            },
            getContentMetadataSetup(media, {
                artwork:          true,
                poster:           true,
                thumbnail:        true,
                endOfVideoPoster: true,
                artworkRatio:     "square",
                posterRatio:      "portrait",
                thumbnailRatio:   "landscape"
            }),
            getSrcSetup(session, media),
            getI18nSetup($i18nService),
            getCastingSetup(session),
            getOffsetStartPositionSetup(session, media, asset, params),
            getGoogleAnalyticsSetup($analyticsService, media),
            getAdSetup(
                session,
                media,
                asset,
                $i18nService,
                $privacyService
            )
        );

        if (!setup.src) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type: "missing_video_source"
            }, this);

            return;
        }

        // These properties together represent the 'media source' for the
        // player. If either one changes after this point, we'll likely need to
        // call `changeMediaSource()`.
        this.set("mediaId", get(media, "media", null));
        this.set("adType", get(session, "ad_type", "none"));

        // Resolve starting point
        setup.offsetStartPosition = setup.offsetStartPosition || 0;
        this.set("startCursor", setup.offsetStartPosition);

        // Setup smartclip pixel
        if (setup.ads && setup.smartclipPixel) {
            this.setSmartclipPixel(setup.smartclipPixel);
            delete setup.smartclipPixel;
        }

        // Set AdTag Url
        if (setup.ads && setup.adTagUrl) {
            this.set("adTagUrl", setup.adTagUrl);
        }

        // Instantiate Player
        this.radiantMediaPlayer = player = new RadiantMP(this.playerNode.id);

        // Bind listeners
        player.on("adblock", this.onAdBlock);
        player.on("adcomplete", this.onAdComplete);
        player.on("adcontentresumerequested", this.onAdContentResumeRequested);
        player.on("adpaused", this.onAdPaused);
        player.on("adresumed", this.onAdPlaying);
        player.on("adstarted", this.onAdPlaying);
        player.on("autoplaymodedetected", this.onAutoplayModeDetected);
        player.on("bufferstalled", this.onBuffer);
        player.on("durationchange", this.onDurationChange);
        player.on("ended", this.onEnded);
        player.on("error", this.onError);
        player.on("loadedmetadata", this.onLoadedMetadata);
        player.on("pause", this.onPause);
        player.on("play", this.onPlay);
        player.on("ready", this.onReady);
        player.on("seeking", this.onSeek);
        player.on("srcchanging", this.onSrcChanging);
        player.on("stopcompleted", this.onStopCompleted);
        player.on("timeupdate", this.onTimeUpdate);
        player.one("destroycompleted", this.onDestroyCompleted);

        // Initialize
        player.init(setup);
    },

    /**
     * Set the new mediaID as new source for the existing player to play.
     *
     * @private
     */
    changeMediaSource: function() {
        var $i18nService    = this.app.$.i18nService,
            $privacyService = this.app.$.privacyService,
            state           = this.state || {},
            media           = get(state, "media", {}),
            asset           = get(state, "asset", {}),
            session         = get(state, "watchingSession", {}),
            params          = get(state, "params", {}),
            mediaId         = get(media, "id", null),
            adType          = get(session, "ad_type", "none"),
            player,
            mediaSource,
            autoplay,
            contentMetadata,
            offsetStartPosition,
            adSetup;

        // Player not ready
        if (!this.state.hasInitialized === 1) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_setup_error",
                message: "Cannot change source. Player isn't ready."
            }, this);

            return;
        }

        // Find Player Instance
        player = this.getRadiantMPInstance();

        if (!player) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_error",
                message: "Missing RadiantMP Instance"
            }, this);
            this.set("isReady", -1);

            return;
        }

        // Already playing correct media
        if (mediaId === this.mediaId
            && adType === this.adType
            && this.isReady === 1) {
            this.autoplay && this.tryAutoPlay();

            return;
        }

        // Determine autoplay
        autoplay = this.autoplay || this.isPlaying;

        // Let's do this
        this.resetPlayerState();
        this.set("isReady", 0);

        // Configuration
        mediaSource         = getSrcSetup(session, media);
        contentMetadata     = getContentMetadataSetup(media, {
            artwork:          true,
            poster:           true,
            thumbnail:        true,
            endOfVideoPoster: true,
            artworkRatio:     "square",
            posterRatio:      "portrait",
            thumbnailRatio:   "landscape"
        }).contentMetadata;
        offsetStartPosition = getOffsetStartPositionSetup(
            session,
            media,
            asset,
            params
        ).offsetStartPosition || 0;
        adSetup             = getAdSetup(
            session,
            media,
            asset,
            $i18nService,
            $privacyService
        );

        // These properties together represent the 'media source' for the
        // player. If either one changes after this point, we'll likely need to
        // call `changeMediaSource()` again.
        this.set("mediaId", mediaId);
        this.set("adType", adType);

        // Binding a one-time handler
        player.one("srcchanged", this.bindSafely(function() {
            this.onSrcChanged(adSetup);
        }));

        player.setContentMetadata(contentMetadata);
        this.set("startCursor", offsetStartPosition);
        this.set("playOnSrcChanged", autoplay);
        player.setShakaDrm(mediaSource.shakaDrm || {});
        player.setSrc(mediaSource.src);
    },

    /**
     * Injects a pixel for smartclip.
     *
     * @param  {string}  pixel  The pixel url
     * @private
     */
    setSmartclipPixel: function(pixel) {
        var tag = this.smartclipPixel;

        tag && this.removeSmartclipPixel();

        tag       = document.createElement("script");
        tag.type  = "text/javascript";
        tag.async = true;
        tag.src   = pixel;
        document.getElementsByTagName("head")[0].appendChild(tag);

        this.set("smartclipPixel", tag);
    },

    /**
     * Removes smartclip pixel.
     *
     * @private
     */
    removeSmartclipPixel: function() {
        var tag = this.smartclipPixel;

        tag && tag.remove();
        this.set("smartclipPixel", null);
    },

    /**
     * Verify that this player can be (re)used to play the supplied media.
     *
     * @param    {?Object}  mediaData    The media data
     * @param    {?Object}  sessionData  The watching session
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function(mediaData, sessionData) {
        var mediaType    = get(mediaData, "type", null),
            assets       = get(mediaData, "assets", []),
            primaryAsset = assets.find(function(asset) {
                return asset.type === "primary";
            }) || null,
            format       = get(primaryAsset, "format", null),
            platform     = get(sessionData, "platform", null);

        if (this.isReusable
            && ["episode", "video"].includes(mediaType)
            && format === "video") {
            return true;
        }

        if (this.isReusable
            && ["episode", "video"].includes(mediaType)
            && format === "external_video"
            && ["streamable", "livestream"].includes(platform)) {
            return true;
        }

        return false;
    },

    /**
     * Get an instance of PlayerApi, to talk with the player in the iframe.
     *
     * @return {Object}  The PlayerApi instance
     */
    getRadiantMPInstance: function() {
        return this.radiantMediaPlayer;
    },

    /**
     * Resets player to the state of an uninitialized player.
     *
     * @private
     */
    resetPlayerState: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.set("isPlayingAds", false);
        };
    }),

    // Radiant MediaPlayer Event Handlers
    // --------------------------------

    /**
     * Ad-block event received.
     *
     * @private
     */
    onAdBlock: function() {
        this.controller.triggerHandler("onMediaPlayerError", {
            type: "ads_required_error"
        }, this);
    },

    /**
     * This handler is used for the `adcomplete` event.
     *
     * @private
     */
    onAdComplete: function() {
        this.set("isPlayingAds", false);
        this.set("playerState", "ad_complete");
    },

    /**
     * @private
     */
    onAdContentResumeRequested: function() {
    },

    /**
     * This handler is used for the `adpaused` event.
     *
     * @private
     */
    onAdPaused: function() {
        this.set("isPlayingAds", false);
        this.set("playerState", "ad_paused");
    },

    /**
     * This handler is used for the `adstarted` and `adresumed` events.
     *
     * @private
     */
    onAdPlaying: function() {
        this.set("isPlayingAds", false);
        this.set("playerState", "ad_playing");
    },

    /**
     * Autoplay capability has become available.
     *
     * @private
     */
    onAutoplayModeDetected: function() {
        var player = this.getRadiantMPInstance();

        if (player) {
            this.set("autoplayMode", player.getAutoplayMode());
            this.tryAutoPlay();
        }
    },

    /**
     * Try to autoplay if the player is ready and autoplayMode should enable us
     * to play with audio.
     *
     * @private
     */
    tryAutoPlay: function() {
        if (!this.autoplay) {
            return;
        }

        if (this.hasStarted) {
            return;
        }

        if (this.isReady === 1 && this.autoplayMode === "autoplay-with-sound") {
            this.play();
        }
    },

    /**
     * This handler is used for the `bufferstalled` event. It fires when the
     * player is loading new media data to play.
     *
     * @private
     */
    onBuffer: function() {
        this.set("playerState", "buffering");
    },

    /**
     * RadiantMP instance has been destroyed.
     *
     * @private
     */
    onDestroyCompleted: function() {
        this.radiantMediaPlayer = null;
        this.removeSmartclipPixel();
        this.resetPlayerState();
        this.set("playerState", "destroyed");
    },

    /**
     * Sends a `duration` event with the last known duration.
     *
     * @public
     */
    onDurationChange: function() {
        var player = this.getRadiantMPInstance();

        if (player) {
            this.duration = player.getDuration() / 1000;
        }
        else {
            this.duration = -1;
        }
    },

    /**
     * This handler is used for the `ended` event. Autoplay next item if
     * possible and desired.
     *
     * @private
     */
    onEnded: function() {
        var adType = this.get("adType");

        switch (adType) {
            case "default":
            case "kids":
            case "playlist":
            case "playlist_kids":
                // This.getRadiantMPInstance().one("adcontentresumerequested", this.bindSafely(function() {
                // console.log("adcontentresumerequested", arguments);
                this.tryNavToNextItem();

                // }));
                break;
            case "none":
            default:
                this.tryNavToNextItem();
                break;
        }
    },

    /**
     * Try navigating to a nex item.
     *
     * @private
     */
    tryNavToNextItem: function() {
        var state     = this.state || {},
            nextMedia = get(state, "nextMedia", null);

        this.set("isPlaying", false);
        this.set("playerState", "ended");

        if (nextMedia && get(config, "autoplay.enabled", false)) {
            this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);
        }
        else {
            this.app.$.autoplayTimerService.stopTimer();
        }
    },

    /**
     * Radiant MediaPlayer encountered an error.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onError: function(event) {
        var player       = this.getRadiantMPInstance(),
            mediaElement = player ? player.getHtmlMediaElement() : null,
            details,
            error,
            type;

        details = {
            event:             event,
            playerErrorData:   player ? player.getErrorData() : null,
            mediaElementError: mediaElement && mediaElement.error ? mediaElement.error : null
        };

        logger.error(details);

        // Fatal Error
        if (details.playerErrorData && details.playerErrorData.fatal) {
            error = new Error("Radiant Media Player - Fatal Error");
            type  = "fatal_player_error";
            this.set("isReusable", false);
        }

        // Setup Error
        else if (this.state.isReady !== 1) {
            error = new Error("Radiant Media Player - Setup Error");
            type  = "player_setup_error";
            this.resetPlayerState();
            this.set("isReusable", false);
        }

        // Player Error
        else {
            // This error denotes a failure to send an event to Google
            // Analytics. We ignore these explicitly because they are very
            // frequently caused by browser plugins.
            if (get(details, "event.playerError.code", null) === 13000) {
                return;
            }

            error = new Error("Radiant Media Player - Player Error");
            type  = "player_error";
        }

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(error, {
                extra: {
                    type:    type,
                    details: circularSafeJSONStringify(details)
                }
            });
        }

        this.controller.triggerHandler("onMediaPlayerError", {
            type:    type,
            message: event.message,
            details: details
        }, this);
    },

    /**
     * This handler is used for the `loadedMetadata` event. Currently just
     * re-broadcasts it. Generally when this event fires, the player is ready to
     * play.
     *
     * @private
     */
    onLoadedMetadata: function() {
        if (this.startCursor) {
            this.seek(this.startCursor);
        }
        this.alignLogo();
    },

    /**
     * This handler is used for the `pause` event. Marks player as not playing
     * and sets it's state to paused.
     *
     * @private
     */
    onPause: function() {
        this.set("isPlaying", false);
        this.set("playerState", "paused");
    },

    /**
     * Tell the autoplayTimerService there was a user interaction whenever the
     * player seeks or gets paused or unpaused.
     *
     * @param  {string}  oldState  The old state
     * @param  {string}  newState  The new state
     * @private
     */
    playerStateChanged: function(oldState, newState) {
        var $player = this.$.player;

        if (oldState !== newState && (oldState === "paused" || newState === "paused")) {
            this.app.$.autoplayTimerService.resetTimer();
        }

        if (newState !== "idle" && newState !== "ended") {
            if (!$player.hasClass("no-poster")) {
                $player.addClass("no-poster");
            }
        }
        else {
            while ($player.hasClass("no-poster")) {
                $player.removeClass("no-poster");
            }
        }
    },

    /**
     * This handler is used for the `play` event. Marks player as playing,
     * provided it has officially been marked as `started`.
     *
     * @private
     */
    onPlay: function() {
        if (this.hasStarted) {
            this.set("isPlaying", true);
            this.set("playerState", "playing");
            this.app.$.autoplayTimerService.startTimer();
        }
    },

    /**
     * This handler is used for the `ready` event.
     *
     * @private
     */
    onReady: function() {
        var changedMediaSource = false,
            mediaId;

        if (this.isReady !== 1) {
            this.set("isReady", 1);

            if (!this.hasInitialized) {
                this.set("hasInitialized", 1);

                if (this.changeMediaSourceOnInitialize) {
                    // A switch of media source is already requested
                    if (this.changeMediaSourceOnInitialize) {
                        mediaId = get(this.state, "watchingSession.source", null);

                        // Only switch if the video is actually different
                        if (mediaId !== this.mediaId) {
                            this.stop();
                            this.changeMediaSource();
                            changedMediaSource = true;
                        }

                        this.changeMediaSourceOnInitialize = false;
                    }
                }
            }

            // If we didn't just change media source, we can try to autoplay our
            // current media item.
            !changedMediaSource && this.tryAutoPlay();

            this.shouldShowControls ? this.showControls() : this.hideControls();
        }
    },

    /**
     * This handler is used for the `seeking` event. If user seeks before the
     * initial `offsetStartPosition` has been reached, we update the
     * `offsetStartPosition` with the new time.
     *
     * @private
     */
    onSeek: function() {
        var player = this.getRadiantMPInstance();

        // Player hasn't officially started yet, meaning the
        // `offsetStartPosition` has not yet been reached.
        if (!this.hasStarted && player) {
            this.startCursor = player.getCurrentTime() / 1000;
        }

        this.set("playerState", "seeking");
    },

    /**
     * This handler is used for the `srcchanged` event. It fires when the player
     * is done loading a new media source.
     *
     * @param  {Object}  adSetup  The ad setup for the new item.
     * @private
     */
    onSrcChanged: function(adSetup) {
        var player = this.getRadiantMPInstance();

        // Update player state
        this.set("isReady", 1);
        this.set("isSrcChanging", false);
        this.set("playerState", "srcchanged");

        // Setup ads
        if (adSetup.ads && adSetup.smartclipPixel) {
            if (adSetup.smartclipPixel !== get(this, "smartclipPixel.src", null)) {
                this.setSmartclipPixel(adSetup.smartclipPixel);
            }
        }
        else {
            this.removeSmartclipPixel();
        }

        if (adSetup.ads && adSetup.adTagUrl) {
            if (adSetup.adTagUrl !== this.adTagUrl) {
                if (player) {
                    player.stopAds();
                    player.loadAds(adSetup.adTagUrl);
                }
                this.set("adTagUrl", adSetup.adTagUrl);
            }
        }
        else {
            // There isn't really any ad 'unloading' API call. To stop
            // displaying ads, add an adTagUrl without placements.
        }

        // Play as needed
        if (this.playOnSrcChanged) {
            this.play();
            this.playOnSrcChanged = false;
        }
    },

    /**
     * This handler is used for the `srcchanging` event. It fires when the
     * player is loading a new media source.
     *
     * @private
     */
    onSrcChanging: function() {
        this.set("hasStarted", false);
        this.set("isSrcChanging", true);
        this.set("playerState", "srcchanging");
    },

    /**
     * This handler is used for the `stopcompleted` event. It marks the player
     * as idle.
     *
     * @private
     */
    onStopCompleted: function() {
        this.set("isPlaying", false);
        this.set("playerState", "idle");
    },

    /**
     * This handler is used for the `timeupdate` event. If the
     * `offsetStartPosition` has been reached, we'll be able to say playback has
     * officially started.
     *
     * @private
     */
    onTimeUpdate: function() {
        var player = this.getRadiantMPInstance(),
            cursor;

        // For some reason this event can fire before onPlayerReady. If it does,
        // we ignore it, so we can properly respond to the first time update
        // when the player is guaranteed to be ready.
        if (this.isReady < 1 || !player) {
            return;
        }

        cursor = player.getCurrentTime() / 1000;
        this.set("cursor", cursor);

        // Player not playing
        if (player.getPaused()) {
            this.set("isSeekable", true);
            this.set("isPlaying", false);

            // But also not seeking
            if (cursor >= this.startCursor) {
                this.set("isPlaying", "paused");
            }
        }

        // Player is playing
        else {
            this.set("isSeekable", true);
            this.set("isPlaying", true);
            this.set("isPlaying", "playing");

            // Which means player has officially started, too
            if (!this.hasStarted) {
                this.set("hasStarted", true);
            }
        }
    },

    // API
    // --------------------------------

    /**
     * Hide the controls.
     *
     * @returns  {boolean}  `true` if hidden, `false` otherwise.
     * @public
     */
    hideControls: function() {
        var player;

        if (this.hasInitialized === 1) {
            player = this.getRadiantMPInstance();
            player && player.setControls(false);

            return true;
        }
        else {
            this.set("shouldShowControls", false);

            return false;
        }
    },

    /**
     * Show the controls.
     *
     * @returns  {boolean}  `true` if showing, `false` otherwise.
     * @public
     */
    showControls: function() {
        var player;

        if (this.hasInitialized === 1) {
            player = this.getRadiantMPInstance();
            player && player.setControls(true);

            return true;
        }
        else {
            this.set("shouldShowControls", true);

            return false;
        }
    },

    /**
     * Start playing
     *
     * @public
     */
    play: function() {
        var player;

        if (this.isReady === 1) {
            player = this.getRadiantMPInstance();
            player && player.play();
        }
    },

    /**
     * Move to a certain cursor position (in seconds)
     *
     * @param  {number}  cursor  The cursor position to seek to
     *
     * @public
     */
    seek: function(cursor) {
        var player;

        if (this.isReady === 1 && this.isSeekable) {
            player = this.getRadiantMPInstance();
            player && player.seek(cursor);
        }
    },

    /**
     * Pause the player
     *
     * @public
     */
    pause: function() {
        var player;

        if (this.isReady === 1) {
            player = this.getRadiantMPInstance();
            player && player.pause();
        }
    },

    /**
     * Stop the player
     *
     * @public
     */
    stop: function() {
        var player;

        if (this.isReady !== 1) {
            // TODO: Do we need to call stopAds() if ads are playing?
            player = this.getRadiantMPInstance();
            player && player.stop();
        }

    },

    /**
     * Returns the current point on the player's timeline in seconds.
     *
     * @return  {?number}  The cursor position
     *
     * @public
     */
    getCursor: function() {
        return new Promise(function(resolve) {
            if (this.get("isReady") === 1) {
                resolve(this.cursor >= 0 ? this.cursor : null);
            }
            else {
                resolve(null);
            }
        });
    },

    /**
     * Returns the duration of the video in seconds.
     *
     * @return  {?number}  The duration of the current asset
     *
     * @public
     */
    getLength: function() {
        return new Promise(function(resolve) {
            if (this.get("isReady") === 1) {
                resolve(this.duration >= 0 ? this.duration : null);
            }
            else {
                resolve(null);
            }
        });
    },

    // Destruction
    // --------------------------------

    /**
     * Destroys the player instance.
     *
     * @private
     */
    destroyPlayer: function() {
        var player = this.getRadiantMPInstance();

        this.stop();

        if (player) {

            // Remove event listeners
            player.off("adblock", this.onAdBlock);
            player.off("adcomplete", this.onAdComplete);
            player.off("adpaused", this.onAdPaused);
            player.off("adresumed", this.onAdPlaying);
            player.off("adstarted", this.onAdPlaying);
            player.off("autoplaymodedetected", this.onAutoplayModeDetected);
            player.off("bufferstalled", this.onBuffer);
            player.off("durationchange", this.onDurationChange);
            player.off("ended", this.onEnded);
            player.off("error", this.onError);
            player.off("loadedmetadata", this.onLoadedMetadata);
            player.off("pause", this.onPause);
            player.off("play", this.onPlay);
            player.off("ready", this.onReady);
            player.off("seeking", this.onSeek);
            player.off("srcchanging", this.onSrcChanging);
            player.off("stopcompleted", this.onStopCompleted);
            player.off("timeupdate", this.onTimeUpdate);

            // Destroy RadiantMP instance
            player.destroy();
        }
        this.set("player", null);

        // Reset internal state
        this.resetPlayerState();
    }

});
